import { formatDate } from '@angular/common';
import { Audit } from '@vsolv/core/audit/domain';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Claim } from '@wsphere/warranties/domain';

export interface AuditCategory {
  entityType: string;
  label: string;
  icon: string;
  theme: ThemeColor;
}

export const AUDIT_CATEGORIES: AuditCategory[] = [
  { entityType: 'claim', label: 'Claim', icon: 'file-06', theme: 'primary' },
  { entityType: 'claim_item', label: 'Jobs', icon: 'tool-02', theme: 'accent' },
  { entityType: 'claim_item_line', label: 'Charges', icon: 'receipt', theme: 'info' },
  { entityType: 'claim_item_hold', label: 'Holds', icon: 'clock-stopwatch', theme: 'warn' },
  { entityType: 'claim_item_escalation', label: 'Escalations', icon: 'flag-03', theme: 'warn' },
  { entityType: 'claim_authorization', label: 'Authorization', icon: 'shield-tick', theme: 'success' },
  { entityType: 'claim_allocation', label: 'Allocations', icon: 'coins-swap-01', theme: 'success' },
  { entityType: 'claim_override', label: 'Approvals', icon: 'check-verified-02', theme: 'warn' },
  { entityType: 'claim_payment', label: 'Payments', icon: 'credit-card-02', theme: 'success' },
  { entityType: 'claim_note', label: 'Notes', icon: 'sticker-square', theme: 'default' },
  { entityType: 'claim_document', label: 'Documents', icon: 'file-attachment-04', theme: 'default' },
];

const FALLBACK_CATEGORY: AuditCategory = { entityType: '', label: 'Record', icon: 'file-06', theme: 'default' };

/** How each action reads after the person's name, e.g. "Jane approved a job". */
const ACTIONS: Record<string, { verb: string; tone: ThemeColor; connector?: string }> = {
  'claim.opened': { verb: 'opened the claim', tone: 'primary' },
  'claim.updated': { verb: 'updated the claim details', tone: 'primary' },
  'claim.resolution_updated': { verb: 'updated the claim resolution', tone: 'primary' },
  'claim.closed': { verb: 'closed the claim', tone: 'success' },
  'claim.cancelled': { verb: 'cancelled the claim', tone: 'danger' },
  'claim.expired': { verb: 'marked the claim as expired', tone: 'warn' },
  'item.created': { verb: 'added a job', tone: 'primary' },
  'item.updated': { verb: 'updated a job', tone: 'primary' },
  'item.deleted': { verb: 'removed a job', tone: 'danger' },
  'item.resolved': { verb: 'resolved a job', tone: 'success' },
  'item.approved': { verb: 'approved a job', tone: 'success' },
  'item.rejected': { verb: 'rejected a job', tone: 'danger' },
  'item.redrafted': { verb: 'moved a job back to draft', tone: 'default' },
  'line.created': { verb: 'added a charge', tone: 'primary' },
  'line.updated': { verb: 'updated a charge', tone: 'primary' },
  'line.adjusted': { verb: 'adjusted the recommendation for a charge', tone: 'info' },
  'line.deleted': { verb: 'removed a charge', tone: 'danger' },
  'hold.opened': { verb: 'put a job on hold', tone: 'warn', connector: 'for' },
  'hold.evidence_attached': { verb: 'attached evidence to a hold', tone: 'info' },
  'hold.completed': { verb: 'completed a hold', tone: 'success' },
  'hold.cancelled': { verb: 'cancelled a hold', tone: 'default' },
  'escalation.opened': { verb: 'escalated a job', tone: 'warn', connector: 'to' },
  'escalation.resolved': { verb: 'resolved an escalation', tone: 'success' },
  'authorization.authorized': { verb: 'authorized the claim', tone: 'success' },
  'authorization.reversed': { verb: 'reversed the authorization', tone: 'danger' },
  'allocation.created': { verb: 'allocated the payment', tone: 'primary', connector: 'for' },
  'allocation.split': { verb: 'changed the payment split', tone: 'primary', connector: 'for' },
  'allocation.reversed': { verb: 'reversed the payment split', tone: 'danger', connector: 'for' },
  'override.requested': { verb: 'asked a manager to approve', tone: 'warn' },
  'override.approved': { verb: 'approved', tone: 'success' },
  'override.rejected': { verb: 'rejected', tone: 'danger' },
  'override.reversed': { verb: 'reversed the approval of', tone: 'danger' },
  'payment.created': { verb: 'created a payment', tone: 'primary', connector: 'of' },
  'payment.updated': { verb: 'updated a payment', tone: 'primary' },
  'payment.issued': { verb: 'issued a payment', tone: 'info', connector: 'of' },
  'payment.voided': { verb: 'voided a payment', tone: 'danger' },
  'payment.completed': { verb: 'marked a payment as completed', tone: 'success' },
  'payment.failed': { verb: 'marked a payment as failed', tone: 'danger' },
  'note.created': { verb: 'added a note', tone: 'primary' },
  'note.updated': { verb: 'edited a note', tone: 'primary' },
  'note.deleted': { verb: 'deleted a note', tone: 'danger' },
  'document.created': { verb: 'uploaded a document', tone: 'primary' },
  'document.updated': { verb: 'updated a document', tone: 'primary' },
  'document.deleted': { verb: 'removed a document', tone: 'danger' },
};

const LABELS: Record<string, string> = {
  content: 'Text',
  feed: 'Visible to',
  frozen: 'Locked for edits',
  coverageId: 'Coverage',
  callerContactChanged: 'Caller contact changed',
  serviceProviderName: 'Service provider',
  repairOrderReference: 'Repair order',
  ownerName: 'Owner',
  mimeType: 'File type',
  reference: 'Authorization',
  authorizationReference: 'Authorization',
  providerReference: 'Provider reference',
  payeeName: 'Payee',
  serviceProviderAmount: 'Service provider share',
  customerAmount: 'Customer share',
  customerName: 'Customer',
  requestedTotal: 'Requested',
  authorizedGross: 'Authorized',
  netPayable: 'Net payable',
  approvedAmount: 'Approved',
  threshold: 'Limit',
};

/** Money is stored in cents. */
const MONEY_KEYS = new Set([
  'amount',
  'requestedAmount',
  'allowedAmount',
  'authorizedAmount',
  'requestedRate',
  'allowedRate',
  'authorizedRate',
  'calculatedTax',
  'requestedTotal',
  'authorizedGross',
  'tax',
  'deductible',
  'netPayable',
  'authorizedPayable',
  'serviceProviderAmount',
  'customerAmount',
  'approvedAmount',
  'threshold',
  'estimatedCost',
  'actualCost',
  'standard',
  'total',
  'goodwill',
  'liabilityLimit',
]);

/** Fields holding enum values; other text shows as stored (e.g. a currency or a repair order number). */
const ENUM_KEYS = new Set(['category', 'type', 'recommendationSource', 'ownerType', 'payeeType', 'reasonCode']);

const SUCCESS_STATUSES = new Set(['APPROVED', 'RESOLVED', 'COMPLETED', 'ISSUED', 'AUTHORIZED', 'CLOSED', 'ACTIVE']);
const DANGER_STATUSES = new Set(['REJECTED', 'FAILED', 'VOIDED', 'CANCELLED', 'EXPIRED', 'REVERSED']);
const WARN_STATUSES = new Set(['ON_HOLD', 'OPEN_HOLD', 'PENDING']);

/** References worth reading; ids aren't shown. */
const READABLE_REFERENCES = [
  'reference',
  'authorizationReference',
  'reasonCode',
  'payeeName',
  'payeeType',
  'serviceProviderName',
  'customerName',
  'version',
  'callerContactChanged',
];
const COUNTED_REFERENCES: Record<string, string> = {
  claimItemIds: 'Jobs',
  deletedLineIds: 'Charges removed with it',
  cancelledHoldIds: 'Holds cancelled with it',
  voidedPaymentIds: 'Draft payments voided',
};

export interface AuditValue {
  text: string;
  /** Wraps on its own line(s), keeping its line breaks. */
  isLong: boolean;
  /** Set for statuses, which show as badges. */
  theme?: ThemeColor;
}

export interface AuditChange {
  label: string;
  before: AuditValue | null;
  after: AuditValue | null;
}

export interface AuditEntryView {
  id: string;
  changedAt: string;
  changedBy: string | null;
  isSystem: boolean;
  verb: string;
  connector: string | null;
  subject: string | null;
  tone: ThemeColor;
  category: AuditCategory;
  /** `event` for entries that record something happening rather than values, e.g. evidence attached. */
  mode: 'created' | 'changed' | 'removed' | 'event';
  changes: AuditChange[];
  amounts: { label: string; value: string }[];
  references: { label: string; value: string }[];
  decidedLines: { description: string; amount: string | null }[];
  files: string[];
  reason: string | null;
  /** The job the entry belongs to, by name. */
  job: string | null;
}

/** Names for ids the entries hold, from what the page already loaded. */
export interface AuditNames {
  coverages?: Map<string, string>;
  jobs?: Map<string, string>;
}

/** Ids read as names where we know them; the rest aren't shown. */
const isIdKey = (key: string) => /Ids?$/.test(key);

/** Internal counters, not something a reader acts on. */
const HIDDEN_FIELDS = new Set(['decisionVersion', 'adjustmentVersion']);

export function toAuditEntryView(audit: Audit.AuditLogDto, names: AuditNames = {}): AuditEntryView {
  const data: Audit.Data = audit.data ?? {};
  const previous = data.previous ?? null;
  const next = data.next ?? null;
  const action = audit.action ?? '';
  const known = ACTIONS[action];
  const mode = previous && next ? 'changed' : previous ? 'removed' : next ? 'created' : 'event';
  const references: Record<string, unknown> = data.references ?? {};
  const currency = typeof data.amounts?.['currency'] === 'string' ? (data.amounts['currency'] as string) : 'USD';
  const isJob = audit.entityType === 'claim_item';
  const jobId = isJob ? audit.entityId : references['claimItemId'];
  const job = typeof jobId === 'string' ? (names.jobs?.get(jobId) ?? null) : null;

  return {
    id: audit.id,
    changedAt: audit.changedAt,
    changedBy: audit.changedBy,
    isSystem: !audit.changedBy || audit.changedBy === Audit.SYSTEM_ACTOR,
    verb: known?.verb ?? (audit.fieldName ? `changed ${humanize(audit.fieldName).toLowerCase()}` : 'made a change'),
    connector: known?.connector ?? null,
    subject: subjectOf(audit, next ?? previous ?? {}, currency) ?? (isJob ? job : null),
    tone: known?.tone ?? 'default',
    category: AUDIT_CATEGORIES.find(category => category.entityType === audit.entityType) ?? FALLBACK_CATEGORY,
    mode,
    changes: changesOf(audit, previous, next, currency, names),
    amounts: Object.entries(data.amounts ?? {})
      .filter(([key, value]) => key !== 'currency' && value !== null && value !== undefined)
      .map(([key, value]) => ({ label: labelOf(key), value: formatValue(key, value, currency)?.text ?? '—' })),
    references: [
      ...READABLE_REFERENCES.filter(key => isPresent(references[key])).map(key => ({
        label: labelOf(key),
        value: formatValue(key, references[key], currency)?.text ?? '',
      })),
      ...Object.entries(COUNTED_REFERENCES)
        .filter(([key]) => Array.isArray(references[key]) && (references[key] as unknown[]).length)
        .map(([key, label]) => ({ label, value: String((references[key] as unknown[]).length) })),
    ],
    decidedLines: Array.isArray(references['lines'])
      ? (references['lines'] as Record<string, unknown>[]).map(line => ({
          description: String(line['description'] ?? 'Charge'),
          amount: isPresent(line['authorizedAmount'])
            ? formatMoney(line['authorizedAmount'] as number, currency)
            : null,
        }))
      : [],
    files: Array.isArray(references['files'])
      ? (references['files'] as { title?: string }[]).map(file => file.title ?? 'File')
      : [],
    reason: audit.reason,
    // A job's own entries already name it.
    job: isJob ? null : job,
  };
}

/** What the entry is about, e.g. a charge's description or a payment's amount. */
function subjectOf(audit: Audit.AuditLogDto, snapshot: Record<string, unknown>, currency: string): string | null {
  const references: Record<string, unknown> = audit.data?.references ?? {};

  switch (audit.entityType) {
    case 'claim_item_escalation':
      return text(snapshot['ownerName']);
    case 'claim_item_hold':
      return text(snapshot['requestedEvidence']) ?? (snapshot['type'] ? humanizeEnum(String(snapshot['type'])) : null);
    case 'claim_authorization':
    case 'claim_allocation':
      return text(references['reference']);
    case 'claim_override': {
      const type = references['overrideType'] as Claim.Override.Type | undefined;
      const threshold = audit.data?.amounts?.['threshold'];
      if (!type) return null;
      return `the ${Claim.Override.describe({ type, threshold: typeof threshold === 'number' ? threshold : null, currency })}`;
    }
    case 'claim_payment': {
      const amount = audit.data?.amounts?.['amount'] ?? snapshot['amount'];
      return typeof amount === 'number' ? formatMoney(amount, currency) : null;
    }
    case 'claim_document':
      return text(snapshot['title']) ?? text(snapshot['fileName']);
    case 'claim_note':
      return text(snapshot['title']) ?? truncate(stripHtml(text(snapshot['content']) ?? '').replace(/\s+/g, ' '), 60);
    default:
      return text(snapshot['description']) ?? text(snapshot['title']);
  }
}

function changesOf(
  audit: Audit.AuditLogDto,
  previous: Record<string, unknown> | null,
  next: Record<string, unknown> | null,
  currency: string,
  names: AuditNames
): AuditChange[] {
  // Entries written before the structured data only have the field columns.
  if (!previous && !next) {
    if (!audit.fieldName) return [];
    return [
      {
        label: labelOf(audit.fieldName),
        before: formatValue(audit.fieldName, audit.beforeValue, currency),
        after: formatValue(audit.fieldName, audit.afterValue, currency),
      },
    ];
  }

  const keys = [...new Set([...Object.keys(previous ?? {}), ...Object.keys(next ?? {})])].filter(
    key => key === 'coverageId' || (!isIdKey(key) && !HIDDEN_FIELDS.has(key))
  );
  const nameOf = (key: string, value: unknown) =>
    key === 'coverageId' && typeof value === 'string' ? (names.coverages?.get(value) ?? 'Unknown coverage') : value;
  // Status first: it's what a reader looks for.
  keys.sort((a, b) => Number(b === 'status') - Number(a === 'status'));

  return (
    keys
      .map(key => ({
        label: labelOf(key),
        before: previous ? formatValue(key, nameOf(key, previous[key]), currency) : null,
        after: next ? formatValue(key, nameOf(key, next[key]), currency) : null,
      }))
      // A change lists what differs; a new or removed record lists only the values it had.
      .filter(change => (previous && next ? change.before?.text !== change.after?.text : change.before || change.after))
  );
}

export function labelOf(key: string) {
  return LABELS[key] ?? humanize(key);
}

function formatValue(key: string, value: unknown, currency: string): AuditValue | null {
  if (!isPresent(value)) return null;

  if (key === 'status' && typeof value === 'string') {
    return { text: humanizeEnum(value), isLong: false, theme: statusTheme(value) };
  }
  if (typeof value === 'number') {
    if (key === 'taxRate') return { text: `${+(value * 100).toFixed(4)}%`, isLong: false };
    return { text: MONEY_KEYS.has(key) ? formatMoney(value, currency) : value.toLocaleString('en-US'), isLong: false };
  }
  if (typeof value === 'boolean') return { text: value ? 'Yes' : 'No', isLong: false };
  if (Array.isArray(value)) {
    const items = value.map(item =>
      item && typeof item === 'object'
        ? summarize(item as Record<string, unknown>, currency)
        : humanizeWord(String(item))
    );
    return { text: items.join(', '), isLong: false };
  }
  if (typeof value === 'object') {
    const summary = summarize(value as Record<string, unknown>, currency);
    return summary ? { text: summary, isLong: false } : null;
  }

  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw) && !isNaN(Date.parse(raw))) {
    const isMidnight = /T00:00:00(\.0+)?Z$/.test(raw);
    return {
      text: formatDate(raw, isMidnight ? 'mediumDate' : 'medium', 'en-US', isMidnight ? 'UTC' : undefined),
      isLong: false,
    };
  }
  if (ENUM_KEYS.has(key)) return { text: humanizeEnum(raw), isLong: false };

  const plain = /<[a-z][\s\S]*>/i.test(raw) ? stripHtml(raw) : raw;
  return { text: plain || '(empty)', isLong: plain.length > 120 || plain.includes('\n') };
}

/** An object on one line, e.g. "Standard $10.00 · Total $12.00"; a lone value reads on its own. */
function summarize(value: Record<string, unknown>, currency: string): string {
  const parts = Object.entries(value)
    .filter(([key]) => !isIdKey(key))
    .map(([key, inner]) => ({ label: labelOf(key), formatted: formatValue(key, inner, currency) }))
    .filter(part => part.formatted);

  if (parts.length === 1) return parts[0].formatted?.text ?? '';
  return parts.map(part => `${part.label}: ${part.formatted?.text}`).join(' · ');
}

function statusTheme(status: string): ThemeColor {
  if (SUCCESS_STATUSES.has(status)) return 'success';
  if (DANGER_STATUSES.has(status)) return 'danger';
  if (WARN_STATUSES.has(status)) return 'warn';
  return 'default';
}

export function formatMoney(cents: number, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }
}

function humanize(key: string) {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\bid\b/g, 'ID');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function humanizeEnum(value: string) {
  return humanize(value.toLowerCase());
}

function humanizeWord(value: string) {
  return /^[a-z]+$/.test(value) ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function stripHtml(html: string) {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h\d)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function truncate(value: string, max: number) {
  if (!value) return null;
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function isPresent(value: unknown) {
  return value !== null && value !== undefined && value !== '';
}
